import { useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { X, ArrowRight, Camera, SkipForward } from "lucide-react";

/**
 * Iter 68: Dialog auto-snapshot pre-avanzamento fase.
 *
 * Suggerisce una milestone semantica appropriata basata sulla fase corrente,
 * e permette all'admin di crearla in un click prima dell'avanzamento.
 *
 * Props:
 *   - open: bool
 *   - leagueId, season, currentPhase, nextPhase
 *   - onClose(): dismisses
 *   - onConfirm(withMilestone: bool): triggered dopo la creazione (o skip)
 */
const PHASE_TO_MILESTONE = {
  // fase corrente → codice milestone suggerito prima dell'avanzamento
  // (semantica: fase 1 = post-import rose iniziale, fase 2 = pre-riparazione, ecc.)
  1: { code: "T0", label: "Rose Iniziali", desc: "Snapshot delle rose subito dopo l'import iniziale." },
  2: { code: "T1", label: "Pre Riparazione", desc: "Fotografia della lega prima del mercato di riparazione." },
  3: { code: "T2", label: "Post Riparazione", desc: "Stato delle rose e budget dopo la chiusura del mercato." },
  4: { code: "T3", label: "Fine Campionato", desc: "Snapshot alla chiusura del campionato, prima dei calcoli economici." },
  9: { code: "T4", label: "Pre Asta Furti", desc: "Stato pre-asta con crediti disponibili per squadra." },
  10: { code: "T5", label: "Post Asta Furti", desc: "Snapshot delle rose subito dopo la conclusione dell'asta furti." },
};

export function suggestMilestoneForPhase(phase) {
  return PHASE_TO_MILESTONE[phase] || null;
}

export default function AutoSnapshotDialog({ open, leagueId, season, currentPhase, nextPhaseLabel, onClose, onConfirm }) {
  const [creating, setCreating] = useState(false);
  const [customLabel, setCustomLabel] = useState("");

  if (!open) return null;
  const suggestion = suggestMilestoneForPhase(currentPhase);

  const createAndAdvance = async () => {
    if (!suggestion) { onConfirm(false); return; }
    setCreating(true);
    try {
      const label = customLabel.trim() || `${suggestion.label} · ${season || ""}`.trim();
      await api.post(`/leagues/${leagueId}/milestones`, {
        code: suggestion.code,
        label,
        season,
        note: "Auto-creato prima dell'avanzamento fase",
      });
      toast.success(`📸 Milestone ${suggestion.code} creata`);
      onConfirm(true);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setCreating(false); }
  };

  const skipAndAdvance = () => onConfirm(false);

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="auto-snapshot-dialog">
      <div onClick={e => e.stopPropagation()}
        className="w-full max-w-lg rounded-3xl bg-gradient-to-br from-slate-900 to-slate-950 border border-purple-500/40 shadow-2xl shadow-purple-500/20 p-6">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-purple-500/20 border border-purple-500/40 flex items-center justify-center">
              <Camera className="text-purple-300" size={22}/>
            </div>
            <div>
              <div className="text-[10px] tracking-[0.3em] text-purple-400 uppercase font-bold">📸 Milestone auto-snapshot</div>
              <h2 className="text-xl font-black text-white mt-0.5">Salva un ricordo prima di avanzare</h2>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-white" data-testid="auto-snapshot-close">
            <X size={20}/>
          </button>
        </div>

        <div className="text-xs text-slate-300 leading-relaxed mb-4 border-l-2 border-purple-400/60 pl-3 bg-purple-500/5 py-2 rounded-r-md">
          Stai per avanzare a <strong className="text-white">{nextPhaseLabel || "prossima fase"}</strong>.
          {suggestion ? (
            <> È il momento perfetto per creare la milestone <strong className="text-purple-300">{suggestion.code} · {suggestion.label}</strong> —
            uno snapshot completo della lega (rose, budget, giocatori) che potrai confrontare in futuro con la timeline.</>
          ) : (
            <> Per questa fase non c&apos;è una milestone predefinita, ma puoi comunque avanzare senza snapshot.</>
          )}
        </div>

        {suggestion && (
          <div className="rounded-2xl border border-purple-500/40 bg-purple-500/10 p-4 mb-4">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-purple-500/20 border border-purple-500/50 font-black text-sm text-purple-200">{suggestion.code}</span>
              <div className="flex-1">
                <div className="font-black text-white text-sm">{suggestion.label}</div>
                <div className="text-[11px] text-slate-400">{suggestion.desc}</div>
              </div>
            </div>
            <div className="mt-3">
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold block mb-1">Etichetta personalizzata (opzionale)</label>
              <input value={customLabel} onChange={e => setCustomLabel(e.target.value)}
                placeholder={`${suggestion.label} · ${season || ""}`}
                data-testid="auto-snapshot-label"
                className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-sm"/>
            </div>
          </div>
        )}

        <div className="flex items-center justify-end gap-2 flex-wrap">
          <button onClick={skipAndAdvance} disabled={creating}
            data-testid="auto-snapshot-skip"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl border border-slate-700 text-slate-300 font-bold text-sm hover:border-slate-500 disabled:opacity-40">
            <SkipForward size={14}/> Salta e avanza
          </button>
          {suggestion && (
            <button onClick={createAndAdvance} disabled={creating}
              data-testid="auto-snapshot-create"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-gradient-to-br from-purple-500 to-purple-700 text-white font-black text-sm hover:brightness-110 shadow-lg shadow-purple-500/30 disabled:opacity-40">
              {creating ? "Creo…" : <>📸 Crea milestone <ArrowRight size={13}/></>}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
